import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpBackendService } from 'src/app/core/backend-services/http/http.backend-service';
import {
  AvatarSettingsInterface,
  Avatar3DInterface,
} from 'src/app/core/interfaces';
import { GenderEnum } from '../../enums';

@Injectable({
  providedIn: 'root',
})
export class ProfileBackendService {
  private readonly BASE_ADDRESS = '/profile';

  /**
   * @description constructor of ProfileBackendService
   * @param {HttpBackendService} http - http backend service
   */
  constructor(private http: HttpBackendService) {}

  /**
   * @description uploads profile picture
   * @param {any} blob
   * @returns {Promise<void>}
   */
  public uploadProfilePicture(blob: any): Promise<void> {
    const data = new FormData();
    data.append('profilePicture', blob);
    return firstValueFrom(
      this.http.post<void>(`${this.BASE_ADDRESS}/upload-profile-picture`, data)
    );
  }

  /**
   * @description saves avatar settings
   * @param {AvatarSettingsInterface} settings
   * @returns {Promise<void>}
   */
  public saveAvatarSettings(settings: AvatarSettingsInterface): Promise<void> {
    return firstValueFrom(
      this.http.post<void>('/profile-picture-avatar', { settings })
    );
  }

  /**
   * @description gets avatar settings
   * @returns {Promise<{settings: AvatarSettingsInterface}>}
   */
  public getAvatarSettings(): Promise<{ settings: AvatarSettingsInterface }> {
    return firstValueFrom(
      this.http.get<{ settings: AvatarSettingsInterface }>(
        '/profile-picture-avatar'
      )
    );
  }

  /**
   * @description generates avatar as profile picture
   * @param {AvatarSettingsInterface} settings
   * @returns {Promise<void>}
   */
  public generateAvatarAsProfilePicture(
    settings: AvatarSettingsInterface
  ): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(`${this.BASE_ADDRESS}/generate-avatar`, { settings })
    );
  }

  /**
   * @description saves 3D avatar settings
   * @param {Avatar3DInterface} avatar
   * @returns {Promise<void>}
   */
  public saveAvatar3DSettings(avatar: Avatar3DInterface): Promise<void> {
    return firstValueFrom(
      this.http.post<void>(`${this.BASE_ADDRESS}/avatar-3d`, { ...avatar })
    );
  }

  /**
   * @description gets 3D avatar settings
   * @returns {Promise<{avatar: Avatar3DInterface}>}
   */
  public getAvatar3DSettings(): Promise<{ avatar3d: Avatar3DInterface }> {
    return firstValueFrom(
      this.http.get<{ avatar3d: Avatar3DInterface }>(
        `${this.BASE_ADDRESS}/avatar-3d`
      )
    );
  }

  /**
   * @description updates user gender
   * @param {GenderEnum} gender
   * @returns {Promise<void>}
   */
  public updateGender(gender: GenderEnum): Promise<void> {
    return firstValueFrom(
      this.http.put<void>(`${this.BASE_ADDRESS}/gender/${gender}`, {})
    );
  }
}
